import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { AppCardTitle, AppEmptyLine, AppInfoButton, AppNote, AppTableSkeleton } from '@components'
import { dsColorNew } from '@palette'

/**
 * Building blocks for the Setup tabs so every screen shares the same
 * spacing, borders and hierarchy. Bypasses the global `MuiPaper`
 * override (which paints Paper `#eeeeee` with no shadow) — those
 * defaults were designed for older admin pages and kill contrast on
 * form-heavy screens like this one.
 */

/** White surface with a real border and consistent padding: the app's card (16px, hairline). */
export function Surface({ children, padded = true, sx = {}, ...rest }) {
	return (
		<Box
			{...rest}
			sx={{
				bgcolor: dsColorNew.white,
				border: `1px solid ${dsColorNew.grayBorder}`,
				borderRadius: '16px',
				overflow: 'hidden',
				...sx,
				...(padded ? { p: 2, ...sx } : {}),
			}}>
			{children}
		</Box>
	)
}

/** Header block for a Surface — the app's card title (`AppCardTitle`: icon tile and title) with the
 *  card's actions on the right, as the cards of My Business and the AutoEurope tool. What the card is
 *  (`subtitle`) and its notes (`info`) open from an ⓘ beside the title, as no other page of the app
 *  opens a card with a paragraph. */
export function SurfaceHeader({ title, subtitle, info, actions, icon, sx = {} }) {
	return (
		<Stack direction="row" alignItems="flex-start" justifyContent="space-between"
		       flexWrap="wrap" gap={1.5} sx={{ mb: 2, ...sx }}>
			<Box sx={{ minWidth: 0, flex: '1 1 320px' }}>
				<AppCardTitle icon={icon} title={title}>
					{(subtitle || info) && <AppInfoButton title={typeof title === 'string' ? title : undefined} sx={{ ml: 0.25 }}>
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25,
						           '& strong, & b': { color: dsColorNew.textTitle, fontWeight: 600 } }}>
							{subtitle && <Box>{subtitle}</Box>}
							{info}
						</Box>
					</AppInfoButton>}
				</AppCardTitle>
			</Box>
			{actions && (
				<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ flexShrink: 0 }}>
					{actions}
				</Stack>
			)}
		</Stack>
	)
}

/** Empty state — used everywhere a table would show "nothing yet". */
/**
 * What a table shows while its rows are still in flight.
 *
 * Every Setup tab used to render its empty state immediately, so going
 * to a tab announced "No price limits yet" for a second or two and then
 * filled with price limits — the screen said the opposite of the truth,
 * which is worse than saying nothing (Curro, 2026-10-03). Same box as
 * `EmptyState` so the layout does not jump when the rows land.
 */
// The app's table skeleton (the pattern every page loads with) instead of a spinner. `what` still
// names the rows for screen readers.
export function LoadingState({ what = 'rows' }) {
	return (
		<Box aria-label={`Loading ${what}…`}>
			<AppTableSkeleton rows={6} columns={6}/>
		</Box>
	)
}

// The app's empty line (folder and muted text, as the data tables) with the tab's own sentence and
// action under it. `icon` is still accepted so the call sites stay as they are; the line has its own.
export function EmptyState({ title, description, action }) {
	return (
		<Box sx={{ py: 2, px: 3, textAlign: 'center' }}>
			<AppEmptyLine sx={{ py: 1 }}>{title}</AppEmptyLine>
			{description && (
				<Typography sx={{ color: dsColorNew.textMuted,
				                  fontSize: 13, maxWidth: 480, mx: 'auto' }}>
					{description}
				</Typography>
			)}
			{action && <Box sx={{ mt: 2 }}>{action}</Box>}
		</Box>
	)
}

/** Section label used inside a Surface to split content into blocks. */
export function SectionLabel({ children, sx = {} }) {
	return (
		<Typography sx={{
			color: dsColorNew.textSubtle, fontWeight: 600,
			fontSize: 11, letterSpacing: 0.6,
			textTransform: 'uppercase',
			...sx,
		}}>
			{children}
		</Typography>
	)
}

/** Explanatory note — the app's info note (`AppNote`). */
export function HelpNote({ children, sx = {} }) {
	return <AppNote sx={sx}>{children}</AppNote>
}

/** Sticky-friendly bar for tab-level actions (Add rule, filters, etc.). */
export function ActionBar({ children, sx = {} }) {
	return (
		<Stack direction="row" alignItems="center" justifyContent="space-between"
		       flexWrap="wrap" gap={1.5}
		       sx={{
			       mb: 2, py: 1.5, px: 2,
			       bgcolor: dsColorNew.white,
			       border: `1px solid ${dsColorNew.grayBorder}`,
			       borderRadius: '12px',
			       ...sx,
		       }}>
			{children}
		</Stack>
	)
}
